import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, ArrowUpRight, Briefcase, MapPin, Calendar, FileText, Share2, Upload } from "lucide-react";
import { api } from "../lib/api";
import Seo from "../components/Seo";

function fmtDate(d) {
  try { return new Date(d).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" }); }
  catch { return d; }
}

export function VacanciesList() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/vacancies").then((r) => { setItems(r.data || []); setLoaded(true); }).catch(() => setLoaded(true));
  }, []);

  const specialisms = ["All", ...Array.from(new Set(items.map((i) => i.specialism)))];
  const filtered = filter === "All" ? items : items.filter((i) => i.specialism === filter);

  return (
    <div data-testid="vacancies-page">
      <Seo title="Vacancies" description="Live construction and infrastructure vacancies across the UK, Europe and the Middle East — Quantity Surveying, Commercial, Contracts & Claims, Dispute Resolution, Planning, Programme & Project Management." path="/vacancies" />

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pt-32 pb-12">
        <div className="overline">Vacancies</div>
        <h1 className="font-serif text-5xl sm:text-7xl mt-6 leading-[0.95] tracking-tight" data-testid="vacancies-headline">
          Live roles across the <span className="italic text-[#1E2A78]">network</span>.
        </h1>
        <p className="text-[#5C5C5C] text-lg mt-6 max-w-2xl">
          A curated list of current mandates across the UK, Europe and the Middle East.
          All vacancies are reviewed by a B7 consultant — apply confidentially.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 sm:px-10 pb-24">
        {specialisms.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-10" data-testid="vacancies-filter-bar">
            {specialisms.map((s) => (
              <button
                key={s}
                onClick={() => setFilter(s)}
                data-testid={`vacancies-filter-${s.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                className={`px-4 py-2 text-[11px] tracking-[0.2em] uppercase font-semibold transition-colors border ${
                  filter === s ? "bg-[#1E2A78] text-white border-[#1E2A78]" : "bg-transparent text-[#1E2A78] border-[#E2E0D9] hover:border-[#1E2A78]"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {!loaded ? (
          <p className="text-[#5C5C5C]">Loading vacancies...</p>
        ) : filtered.length === 0 ? (
          <div className="bg-white border border-[#E2E0D9] p-12 text-center" data-testid="vacancies-empty">
            <Briefcase size={32} className="mx-auto text-[#1E2A78]" />
            <h2 className="font-serif text-3xl mt-4">No active vacancies right now.</h2>
            <p className="text-[#5C5C5C] mt-3">
              We're often briefed on confidential mandates that don't make this page.
            </p>
            <div className="mt-8 flex flex-wrap gap-3 justify-center">
              <Link to="/submit-cv" className="btn-primary" data-testid="vacancies-empty-cv-btn">Submit CV</Link>
              <Link to="/contact" className="btn-secondary" data-testid="vacancies-empty-contact-btn">Contact us</Link>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filtered.map((v) => (
              <Link
                key={v.id}
                to={`/vacancies/${v.id}`}
                className="group block bg-white border border-[#E2E0D9] p-8 hover:border-[#1E2A78] transition-colors"
                data-testid={`vacancy-card-${v.id}`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="overline">{v.specialism}</div>
                  <ArrowUpRight size={20} className="text-[#1E2A78] group-hover:translate-x-1 transition-transform" />
                </div>
                <h3 className="font-serif text-2xl sm:text-3xl mt-3 leading-tight group-hover:text-[#1E2A78] transition-colors">
                  {v.role_title}
                </h3>
                <p className="text-sm text-[#5C5C5C] mt-2">{v.company_name}</p>
                <div className="flex flex-wrap gap-4 mt-6 text-xs text-[#5C5C5C]">
                  <span className="inline-flex items-center gap-1.5"><MapPin size={14} className="text-[#2EA3F2]" />{v.location}</span>
                  {v.contract_type && <span className="inline-flex items-center gap-1.5"><FileText size={14} className="text-[#2EA3F2]" />{v.contract_type}</span>}
                  <span className="inline-flex items-center gap-1.5"><Calendar size={14} className="text-[#2EA3F2]" />Posted {fmtDate(v.created_at)}</span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </section>
    </div>
  );
}

export function VacancyDetail() {
  const { id } = useParams();
  const [v, setV] = useState(null);
  const [missing, setMissing] = useState(false);
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", cv_link: "", cover_note: "" });
  const [cvFile, setCvFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [applied, setApplied] = useState(false);

  useEffect(() => {
    api.get(`/vacancies/${id}`).then((r) => setV(r.data)).catch(() => setMissing(true));
  }, [id]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const apply = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { toast } = await import("sonner");
      const fd = new FormData();
      fd.append("full_name", form.full_name);
      fd.append("email", form.email);
      if (form.phone) fd.append("phone", form.phone);
      if (form.cv_link) fd.append("cv_link", form.cv_link);
      if (form.cover_note) fd.append("cover_note", form.cover_note);
      if (cvFile) fd.append("cv", cvFile);
      await api.post(`/vacancies/${id}/apply`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setApplied(true);
      toast.success("Application sent — a consultant will be in touch.");
    } catch (err) {
      const { toast } = await import("sonner");
      const status = err?.response?.status;
      if (status === 429) {
        toast.error(err.response.data?.detail || "Too many attempts — please wait a few minutes.");
      } else if (status === 404) {
        toast.error("This role has just closed.");
      } else if (status === 413) {
        toast.error("CV file too large — max 10MB.");
      } else if (status === 415) {
        toast.error("Only PDF, DOC, or DOCX files accepted.");
      } else {
        toast.error("Application failed. Please try again.");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const shareOnLinkedIn = () => {
    const url = window.location.href;
    const text = `${v?.role_title} — ${v?.company_name} · ${v?.location}`;
    window.open(`https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}&summary=${encodeURIComponent(text)}`, "_blank", "noopener");
  };

  if (missing) {
    return (
      <div className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-center" data-testid="vacancy-not-found">
        <h1 className="font-serif text-4xl">Vacancy unavailable.</h1>
        <p className="text-[#5C5C5C] mt-4">This role may have closed or been filled.</p>
        <Link to="/vacancies" className="link-underline mt-6 inline-block">Back to vacancies</Link>
      </div>
    );
  }
  if (!v) return <div className="max-w-3xl mx-auto px-6 pt-40 pb-32 text-[#5C5C5C]">Loading...</div>;

  return (
    <div data-testid="vacancy-detail-page">
      <Seo title={v.role_title} description={`${v.role_title} at ${v.company_name} — ${v.location}. ${v.specialism}.`} path={`/vacancies/${v.id}`} />

      <section className="max-w-3xl mx-auto px-6 sm:px-10 pt-32 pb-12">
        <Link to="/vacancies" className="inline-flex items-center gap-2 overline" data-testid="vacancy-back-link">
          <ArrowLeft size={14} /> Vacancies
        </Link>
        <div className="overline mt-10">{v.specialism}</div>
        <h1 className="font-serif text-4xl sm:text-6xl mt-4 leading-[1.02] tracking-tight">{v.role_title}</h1>
        <p className="text-lg text-[#5C5C5C] mt-4">{v.company_name}</p>

        <div className="mt-6">
          <button
            type="button"
            onClick={shareOnLinkedIn}
            className="inline-flex items-center gap-2 px-4 py-2 text-xs tracking-[0.2em] uppercase font-semibold border border-[#1E2A78] text-[#1E2A78] hover:bg-[#1E2A78] hover:text-white transition-colors"
            data-testid="vacancy-share-linkedin"
            title="Share on LinkedIn"
          >
            <Share2 size={14} /> Share on LinkedIn
          </button>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-10 border-t border-[#E2E0D9] pt-8">
          <div>
            <div className="overline">Location</div>
            <div className="mt-2 text-sm">{v.location}</div>
          </div>
          {v.contract_type && (
            <div>
              <div className="overline">Type</div>
              <div className="mt-2 text-sm">{v.contract_type}</div>
            </div>
          )}
          {v.salary_range && (
            <div>
              <div className="overline">Package</div>
              <div className="mt-2 text-sm">{v.salary_range}</div>
            </div>
          )}
          <div>
            <div className="overline">Posted</div>
            <div className="mt-2 text-sm">{fmtDate(v.created_at)}</div>
          </div>
        </div>
      </section>

      <article className="max-w-3xl mx-auto px-6 sm:px-10 pb-12 text-lg leading-relaxed text-[#222] whitespace-pre-line">
        {v.description}
      </article>

      <section className="max-w-3xl mx-auto px-6 sm:px-10 pb-24 border-t border-[#E2E0D9] pt-10">
        <div className="overline">Apply</div>
        <h2 className="font-serif text-3xl mt-3" data-testid="vacancy-apply-headline">Apply for this role.</h2>
        <p className="text-sm text-[#5C5C5C] mt-2">
          One-click apply — your details are sent to a B7 consultant immediately.
        </p>

        {applied ? (
          <div className="bg-white border border-[#E2E0D9] p-10 mt-8" data-testid="vacancy-apply-success">
            <div className="overline">Received</div>
            <h3 className="font-serif text-2xl mt-3">Application sent.</h3>
            <p className="text-[#5C5C5C] mt-3">
              We'll review your application and a consultant will be in touch shortly.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/vacancies" className="btn-secondary" data-testid="vacancy-apply-more-btn">More vacancies</Link>
            </div>
          </div>
        ) : (
          <form onSubmit={apply} className="space-y-6 mt-8" data-testid="vacancy-apply-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <input className="field" placeholder="Full name *" required value={form.full_name} onChange={update("full_name")} data-testid="vacancy-apply-name-input" />
              <input className="field" placeholder="Email *" type="email" required value={form.email} onChange={update("email")} data-testid="vacancy-apply-email-input" />
              <input className="field" placeholder="Phone (optional)" value={form.phone} onChange={update("phone")} data-testid="vacancy-apply-phone-input" />
              <input className="field" placeholder="Link to CV (Drive, Dropbox, LinkedIn)" value={form.cv_link} onChange={update("cv_link")} data-testid="vacancy-apply-cv-input" />
            </div>
            <div>
              <label className="overline block mb-2">Attach CV (PDF, DOC or DOCX — optional, max 10MB)</label>
              <label
                htmlFor="vacancy-apply-cv-file"
                className="inline-flex items-center gap-2 px-4 py-3 border border-[#E2E0D9] bg-white text-sm text-[#1E2A78] hover:border-[#1E2A78] cursor-pointer transition-colors"
                data-testid="vacancy-apply-cv-file-label"
              >
                <Upload size={16} />
                <span>{cvFile ? cvFile.name : "Choose file"}</span>
              </label>
              <input
                id="vacancy-apply-cv-file"
                type="file"
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                onChange={(e) => setCvFile(e.target.files?.[0] || null)}
                className="hidden"
                data-testid="vacancy-apply-cv-file-input"
              />
              {cvFile && (
                <button
                  type="button"
                  onClick={() => setCvFile(null)}
                  className="ml-3 text-xs text-[#5C5C5C] underline hover:text-[#1E2A78]"
                  data-testid="vacancy-apply-cv-file-clear"
                >
                  Remove
                </button>
              )}
            </div>
            <textarea className="field" placeholder="Cover note (optional)" rows={4} value={form.cover_note} onChange={update("cover_note")} data-testid="vacancy-apply-note-input" />
            <div className="flex flex-wrap gap-3">
              <button type="submit" disabled={submitting} className="btn-primary" data-testid="vacancy-apply-submit-button">
                {submitting ? "Sending..." : "Send application"}
              </button>
              <Link to="/contact" className="btn-secondary" data-testid="vacancy-apply-contact-btn">
                Speak to a consultant instead
              </Link>
            </div>
          </form>
        )}
      </section>
    </div>
  );
}
